import { ChevronDown, Sparkles, Trophy } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Dialog, EmptyState } from './ui';
import { PublicMedalistCard } from './PublicMedalistSlider';
import type { PublicMedalist } from '../services/publicHighlights';

type PublicHighlightsDialogProps = {
  open: boolean;
  onClose: () => void;
  medalists: PublicMedalist[];
  loading: boolean;
};

const categories = [
  { id: 'multi', label: 'Multimedalhistas', title: 'Legado de Conquistas' },
  { id: 'diamond', label: 'Diamante', title: 'Elite Diamante' },
  { id: 'platinum', label: 'Platina', title: 'Destaque Platina' },
  { id: 'gold', label: 'Ouro', title: 'Excelência Dourada' },
  { id: 'silver', label: 'Prata', title: 'Ascensão Prateada' },
  { id: 'bronze', label: 'Bronze', title: 'Conquista de Bronze' },
  { id: 'honor', label: 'Honra ao Mérito', title: 'Reconhecimento de Excelência' },
  { id: 'other', label: 'Outras premiações', title: 'Outras Conquistas' },
] as const;

const normalize = (value: string) => value.trim().toLocaleLowerCase('pt-BR');
const studentKey = (medalist: PublicMedalist) => `${normalize(medalist.studentName)}|${normalize(medalist.className)}`;
export function PublicHighlightsDialog({ open, onClose, medalists, loading }: PublicHighlightsDialogProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const model = useMemo(() => {
    const counts = medalists.reduce((items, medalist) => items.set(studentKey(medalist), (items.get(studentKey(medalist)) ?? 0) + 1), new Map<string, number>());
    const sorted = [...medalists].sort((a, b) => a.studentName.localeCompare(b.studentName, 'pt-BR'));

    const grouped = new Map<string, PublicMedalist[]>();
    sorted.forEach((medalist) => {
      const category = getAwardCategoryId(medalist.award);
      const items = grouped.get(category) ?? [];
      items.push(medalist);
      grouped.set(category, items);

      if ((counts.get(studentKey(medalist)) ?? 0) < 2) return;
      const multi = grouped.get('multi') ?? [];
      if (!multi.some((item) => studentKey(item) === studentKey(medalist))) multi.push(medalist);
      grouped.set('multi', multi);
    });
    return { counts, grouped };
  }, [medalists]);

  const visibleCategories = categories.filter((category) => model.grouped.has(category.id));

  return (
    <Dialog
      open={open}
      title="Mural de conquistas 2026"
      description="Medalhistas publicados pelo Colégios Univap, reunidos em um só lugar."
      onClose={onClose}
      size="large"
      centered
      className="public-highlights-dialog"
    >
      {loading ? (
        <div className="public-highlights-loading" role="status"><Sparkles size={20} /> Preparando o mural...</div>
      ) : visibleCategories.length ? (
        <div className="public-highlights-sections">
          {visibleCategories.map((category) => {
            const items = model.grouped.get(category.id) ?? [];
            const isCollapsed = collapsed.has(category.id);
            const contentId = `public-highlight-category-${category.id}`;
            return (
              <section className="highlight-class" key={category.id}>
                <div className="highlight-class-heading">
                  <button type="button" className="highlight-category-toggle" aria-expanded={!isCollapsed} aria-controls={contentId} onClick={() => setCollapsed((current) => {
                    const next = new Set(current);
                    if (next.has(category.id)) next.delete(category.id);
                    else next.add(category.id);
                    return next;
                  })}>
                    <span><span className="eyebrow">{category.label}</span><strong>{category.title}</strong></span>
                    <ChevronDown size={20} aria-hidden="true" />
                  </button>
                  <span>{items.length} {items.length === 1 ? 'aluno' : 'alunos'}</span>
                </div>
                <div className="public-highlights-card-grid" id={contentId} hidden={isCollapsed}>
                  {items.map((medalist) => {
                    const isMulti = category.id === 'multi';
                    const key = studentKey(medalist);
                    return (
                      <PublicMedalistCard medalist={medalist} multi={isMulti} medalCount={model.counts.get(key)} key={`${category.id}-${medalist.id}`} />
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <EmptyState icon={<Trophy />} title="O mural aguarda novas conquistas" description="As medalhas aparecerão aqui depois que os resultados forem publicados pela escola." />
      )}
    </Dialog>
  );
}

function getAwardCategoryId(award: string) {
  const normalized = normalize(award);
  if (normalized.includes('diamante')) return 'diamond';
  if (normalized.includes('platina')) return 'platinum';
  if (normalized.includes('ouro')) return 'gold';
  if (normalized.includes('prata')) return 'silver';
  if (normalized.includes('bronze')) return 'bronze';
  if (normalized.includes('honra ao mérito')) return 'honor';
  return 'other';
}
